import { useRouter } from 'next/router';
import Script from 'next/script';
import { useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { GlobalStyles } from '@vimeo/core/components/GlobalStyles';
import { ThemeProvider } from '@vimeo/core/components/ThemeProvider';
import { CacheContext } from '@vimeo/core/hooks/use-api-resource';
import { Cache } from '@vimeo/core/libs/cache';
import { useEnterpriseSiteRedirect } from '@vimeo/enterprise-site-redirect';
import { GctlProvider } from '@vimeo/gctl-api/hooks/GctlProvider';
import { EmbedShowcaseContext, ShowcaseAuthorization } from '@vimeo/showcase-view';
import { useViewer } from '@vimeo/viewer';
import { useViewerShowcasesRelease, ViewerShowcasesAuthorization } from '@vimeo/viewer-showcases';

interface Params {
  albumId: string;
}

interface ShowcasePageProps {
  match?: RouteComponentProps<Params>['match'];
  history?: RouteComponentProps<Params>['history'];
}

export default function ShowcasePage({ match, history }: ShowcasePageProps) {
  const router = useRouter();
  const { isViewerShowcases } = useViewerShowcasesRelease();
  const cache = useRef(new Cache({ maxSize: 512 }));
  const viewer = useViewer();

  useEnterpriseSiteRedirect(viewer?.teamUser?.enterpriseSiteDomain);

  const { entityId } = router.query;

  if (Array.isArray(entityId)) {
    throw new Error('Invalid query params');
  }

  if (!match || !history || !entityId) {
    return null;
  }

  if (viewer === null || isViewerShowcases === null) return <></>;

  let playerAPIScriptUrl = '';
  if (typeof window !== 'undefined') {
    playerAPIScriptUrl = window.playerAssetUrls?.player_api_js;
  }

  const getReferrerValue = () => {
    try {
      if (document?.referrer) {
        return document.referrer;
      }

      return document?.querySelector('meta[name="referrer"]')?.getAttribute('content') || '';
    } catch (error) {
      return '';
    }
  };

  return (
    <EmbedShowcaseContext.Provider
      value={{ isShowcaseEmbeded: true, referrer: getReferrerValue() }}
    >
      <CacheContext.Provider value={cache.current}>
        <GlobalStyles />
        <EmbedGlobalStyles />

        {isViewerShowcases ? (
          <>
            {playerAPIScriptUrl ? <Script id="player-api" src={playerAPIScriptUrl} /> : null}

            <GctlProvider
              xVimeoPage={router.pathname}
              jwt={viewer?.jwt}
              baseUrl={viewer ? `https://${viewer.apiUrl}` : ''}
              locale={viewer?.locale}
            >
              <ThemeProvider>
                <ViewerShowcasesAuthorization albumIdOrUrl={entityId} isShowcaseEmbedded />
              </ThemeProvider>
            </GctlProvider>
          </>
        ) : (
          <Container>
            <ShowcaseAuthorization
              viewer={viewer}
              match={match}
              history={history}
              location={history.location}
            />
          </Container>
        )}
      </CacheContext.Provider>
    </EmbedShowcaseContext.Provider>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

const EmbedGlobalStyles = createGlobalStyle`
  a {
    text-decoration: none;
  }
  :where(*, *::before, *::after) {
    border-width: 0;
    border-style: solid;
    box-sizing: border-box;
    word-wrap: break-word;
  }
`;
